export type CopilotToolset =
  | "attendance"
  | "attendance_setup"
  | "feedback"
  | "goals"
  | "kudos"
  | "leave"
  | "leave_setup"
  | "people"
  | "pulse"
  | "reviews"
  | "todos";

/**
 * Declared on a route as `staticData.copilot`. `params` lists the route params forwarded under the
 * same name, and marks a record page whose label may carry the record name. `searchParams` maps a
 * wire name to the URL search key it is read from.
 */
export interface CopilotRouteMetadata {
  toolset: CopilotToolset;
  label: string;
  starters: readonly string[];
  params?: readonly string[];
  searchParams?: Readonly<Record<string, string>>;
}

/** The `page` field of an initial message request (shore wire contract). */
export interface CopilotPageHint {
  label: string;
  toolset: CopilotToolset;
  params: Record<string, string>;
}

export interface CopilotPageContext {
  /** Route id plus forwarded params: dismissal is remembered per identity. */
  identity: string;
  hint: CopilotPageHint;
  starters: readonly string[];
}

export interface CopilotMatchLike {
  routeId: string;
  params: Readonly<Record<string, unknown>>;
  search?: Readonly<Record<string, unknown>>;
  staticData?: { copilot?: CopilotRouteMetadata };
}

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    copilot?: CopilotRouteMetadata;
  }
}

// reason: the bounds and patterns below mirror the shore wire contract by hand; when the server
// changes, change them together: pulsewise-shore/src/modules/copilot/turn/copilot-page-context.ts
// (toolset param allowlist, patterns) and copilot-turn.dto.ts (length and count bounds).
export const COPILOT_PAGE_BOUNDS = {
  labelMaxLength: 80,
  toolsetMaxLength: 32,
  maxParams: 4,
} as const;

const TOOLSET_PATTERN = /^[a-z_]+$/;
const PARAM_NAME_PATTERN = /^[A-Za-z]{1,32}$/;
const PARAM_VALUE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const LABEL_FORBIDDEN_PATTERN = /[<>\p{C}]/u;

export function isValidCopilotPageHint(hint: CopilotPageHint): boolean {
  const label = hint.label.trim();
  const entries = Object.entries(hint.params);
  return (
    label === hint.label &&
    label.length >= 1 &&
    label.length <= COPILOT_PAGE_BOUNDS.labelMaxLength &&
    !LABEL_FORBIDDEN_PATTERN.test(label) &&
    hint.toolset.length <= COPILOT_PAGE_BOUNDS.toolsetMaxLength &&
    TOOLSET_PATTERN.test(hint.toolset) &&
    entries.length <= COPILOT_PAGE_BOUNDS.maxParams &&
    entries.every(
      ([name, value]) => PARAM_NAME_PATTERN.test(name) && PARAM_VALUE_PATTERN.test(value),
    )
  );
}

const SUBJECT_SEPARATOR = " \u00b7 ";

function truncateWithEllipsis(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  let kept = "";
  for (const character of text) {
    if (kept.length + character.length > maxLength - 3) break;
    kept += character;
  }
  return `${kept.trimEnd()}...`;
}

/**
 * "<record name> · <area>" within the label bound. The name is page data that can hold any
 * character, so characters the shore rejects become spaces and the name is shortened to fit.
 */
export function labelWithSubject(area: string, subject: string | null | undefined): string {
  const name = (subject ?? "")
    .replace(/[<>\p{C}]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!name) return area;
  const nameBudget = COPILOT_PAGE_BOUNDS.labelMaxLength - area.length - SUBJECT_SEPARATOR.length;
  return `${truncateWithEllipsis(name, nameBudget)}${SUBJECT_SEPARATOR}${area}`;
}

function searchParamsFromMatch(
  match: CopilotMatchLike,
  searchParams: CopilotRouteMetadata["searchParams"],
): Record<string, string> {
  const forwarded: Record<string, string> = {};
  for (const [wireName, searchKey] of Object.entries(searchParams ?? {})) {
    const value = match.search?.[searchKey];
    // A filter the wire cannot carry is only dropped: the page is still identified without it.
    if (typeof value === "string" && PARAM_VALUE_PATTERN.test(value)) forwarded[wireName] = value;
  }
  return forwarded;
}

/**
 * Deepest match carrying copilot metadata wins. A route param value the wire cannot
 * carry drops the whole context: the server would reject the request with a 400,
 * and sending a partial hint would point the model at the wrong page. `subject` is the
 * name of the record the page loaded; it only refines the label of a record page.
 */
export function copilotPageContextFromMatches(
  matches: readonly CopilotMatchLike[],
  subject?: string | null,
): CopilotPageContext | null {
  const match = matches.findLast((candidate) => candidate.staticData?.copilot);
  const metadata = match?.staticData?.copilot;
  if (!match || !metadata) return null;

  const params: Record<string, string> = {};
  for (const name of metadata.params ?? []) {
    const value = match.params[name];
    if (typeof value !== "string") return null;
    params[name] = value;
  }
  // Dismissal is per page, so filters and the record name must not change the identity.
  const sortedParams = Object.entries(params).sort(([a], [b]) => a.localeCompare(b));
  const identity = JSON.stringify([match.routeId, sortedParams]);

  const label = metadata.params?.length
    ? labelWithSubject(metadata.label, subject)
    : metadata.label;
  const hint: CopilotPageHint = {
    label,
    toolset: metadata.toolset,
    params: { ...params, ...searchParamsFromMatch(match, metadata.searchParams) },
  };
  if (!isValidCopilotPageHint(hint)) return null;

  return { identity, hint, starters: metadata.starters };
}

export function isSameCopilotPageContent(
  a: CopilotPageContext | null,
  b: CopilotPageContext | null,
): boolean {
  return a?.identity === b?.identity && JSON.stringify(a?.hint) === JSON.stringify(b?.hint);
}
